import { fn, hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { PrismButton, PrismLoadingBar } from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import type { Hotel } from 'frontend-utils/models';
import type { RatesParams } from 'lumen/components/market/rates/tables/table-market-rates';
import type { Rate, Ratesummary } from 'lumen/models';
import { RATESUMMARY } from 'lumen/models/ratesummary';

export interface CustomColumn extends CustomEmberTableColumn {
  changeRowId: (rowId: string) => void;
  isRowExpanded: (marketId: string) => boolean;
  isRowLoading: (marketId: string) => boolean;
  params: RatesParams;
}

interface MarketRatesTablesCellsTdHotelnameSignature {
  Args: {
    cellData: Ratesummary | Rate;
    column: CustomColumn;
  };
}

export default class MarketRatesTablesCellsTdHotelname extends Component<MarketRatesTablesCellsTdHotelnameSignature> {
  isExpanded = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasData() {
    return this.isRatesummary ? this.ratesummary : this.rate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOfOwnHotelGroup() {
    const ratesummary = this.rate.ratesummary as Ratesummary;
    const hotelForRatesummary = ratesummary.market.hotel as Hotel;
    return (
      this.rate.hotelinfo.hotelGroup ===
      hotelForRatesummary.hotelinfo.hotelGroup
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRatesummary() {
    return this.args.cellData.modelName === RATESUMMARY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRowExpanded() {
    return this.args.column.isRowExpanded(this.ratesummary.market.id!);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRowLoading() {
    if (!this.isRatesummary) {
      return false;
    }

    return this.args.column.isRowLoading(this.ratesummary.market.id!);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rate() {
    return this.args.cellData as Rate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesummary() {
    return this.args.cellData as Ratesummary;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get title() {
    if (this.isRatesummary) {
      return this.ratesummary.market.displayName;
    }
    return this.rate.hotelinfo.name;
  }

  <template>
    <div class="flex items-center truncate">
      {{#if this.hasData}}
        {{#if this.isRatesummary}}
          <PrismButton
            @buttonType="ghost"
            @icon={{if this.isRowExpanded "chevron-down" "chevron-right"}}
            @isSmall={{true}}
            {{! @glint-expect-error: No overload matches this call. }}
            @onClick={{fn @column.changeRowId this.ratesummary.market.id}}
          />

          <LinkTo
            @model={{this.ratesummary.market.hotelId}}
            @query={{hash
              los=@column.params.los
              maxPersons=@column.params.maxPersons
              mealPlan=@column.params.mealPlan
              membershipType=@column.params.membershipType
              month=@column.params.month
              otaId=@column.params.otaId
              rateParam=@column.params.rateParam
            }}
            @route="hotel.rates"
            class="ml-100 flex items-center truncate"
            target="_blank"
          >
            <p
              class="text-200--medium truncate"
              title={{this.title}}
            >{{this.title}}</p>
          </LinkTo>
        {{else}}
          <p
            class="text-200--medium ml-1000 truncate
              {{if this.isOfOwnHotelGroup 'font-bold'}}"
            title={{this.title}}
          >
            {{this.title}}
          </p>
        {{/if}}
        {{#if this.isRowLoading}}
          <div class="row-loading-bar">
            <PrismLoadingBar />
          </div>
        {{/if}}
      {{else}}
        <p class="text-200--medium">
          {{t "marketrates.no_available_compset"}}
        </p>
      {{/if}}
    </div>
  </template>
}
